import { BOOLEAN_VALUE } from './types';

const NUMERIC_INPUT_VALIDATION_REGEX = /^\d*\.?\d*$/;

export function toBooleanValueFields<Data extends object>(
    data: Data,
    columns: (keyof Data)[],
) {
    if (!data) return data;

    const dataCopy = { ...data };

    columns.forEach((column) => {
        if (!Object.prototype.hasOwnProperty.call(dataCopy, column)) return;

        const value = Reflect.get(dataCopy, column);
        const isBoolean = typeof value === 'boolean';
        const newValue = value ? BOOLEAN_VALUE.TRUE : BOOLEAN_VALUE.FALSE;

        Reflect.set(dataCopy, column, isBoolean ? newValue : value);
    });

    return dataCopy;
}

export function fromBooleanValueFields<Data extends object>(
    data: Data,
    columns: (keyof Data)[],
) {
    if (!data) return data;

    const dataCopy = { ...data };

    columns.forEach((column) => {
        if (!Object.prototype.hasOwnProperty.call(dataCopy, column)) return;

        const value = Reflect.get(dataCopy, column);

        const isBooleanValue =
            value === BOOLEAN_VALUE.TRUE || value === BOOLEAN_VALUE.FALSE;

        const newValue = value === BOOLEAN_VALUE.TRUE;

        Reflect.set(dataCopy, column, isBooleanValue ? newValue : value);
    });

    return dataCopy;
}

export function formatNumber(value: number, decimalPlaces = 0): string {
    if (!decimalPlaces) {
        return new Intl.NumberFormat('en-NG', {
            maximumFractionDigits: 0,
        }).format(Math.floor(value));
    }

    return new Intl.NumberFormat('en-NG', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(value);
}

export function formatAmount(amount: number, dp = 2) {
    return Number(amount.toFixed(dp));
}

export function isTinyFloatingPointError(value: number) {
    return Number.isFinite(value) && Math.abs(value) < 1e-9;
}

export function parseDigitsFromString(digitString = '') {
    return Number(
        digitString.replace(/[^0-9.]/g, '').replace(/\.(?=.*\.)/g, ''),
    );
}

interface ValidateInputOptions {
    min: number;
    max: number;
    maxDecimalPlaces: number;
    disallowOutOfRangeValues?: boolean;
}

export function validateNumericInput(
    inputValue: string,
    options: ValidateInputOptions,
) {
    const { max, min, maxDecimalPlaces, disallowOutOfRangeValues } = options;

    if (inputValue === '' || inputValue === '0') {
        return true;
    }

    if (!NUMERIC_INPUT_VALIDATION_REGEX.test(inputValue)) {
        return false;
    }

    if (maxDecimalPlaces === 0 && inputValue.includes('.')) {
        return false;
    }

    const inputValueNum = Number(inputValue);

    if (isNaN(inputValueNum)) {
        return false;
    }

    const isOutOfRange = inputValueNum > max || inputValueNum < min;

    if (isOutOfRange && disallowOutOfRangeValues) {
        return false;
    }

    const decimalPlaces = inputValue.split('.')[1]?.length || 0;
    return decimalPlaces <= maxDecimalPlaces;
}

export const formatNumericInput = (
    value: string,
    valdiateOptions: ValidateInputOptions,
) => {
    const rawValue = value.replace(/,/g, '');

    if (!validateNumericInput(rawValue, valdiateOptions)) {
        return;
    }

    const [whole, decimal] = rawValue.split('.');

    const formattedWhole = whole === '' ? '' : Number(whole).toLocaleString();

    const formattedValue =
        decimal !== undefined ? `${formattedWhole}.${decimal}` : formattedWhole;

    return formattedValue;
};
